import React, { useCallback, useEffect, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { QuickCheckout } from 'components/payment/quick-checkout/quick-checkout';
import { ReceiptBooking } from 'components/receipt-booking';
import { BOOKING_ADD_ONS_PARAM, BOOKING_ADD_ONS_SEGMENT } from 'hooks/use-booking-add-ons-offer.hook';
import BookingAddOnsPage from 'pages/booking-add-ons/booking-add-ons';
import BookingAddOnsDialog from 'pages/booking-add-ons/dialog/booking-add-ons-dialog';
import { StepHeaderProvider } from 'pages/booking-add-ons/step-header.context';
import { Outlet, Route, Routes } from 'react-router-dom';
import { useMiniappSdk } from '../miniapp-sdk-provider/miniapp-sdk.hook';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { Receipt } from '../payment/receipt';
import { BookingCompleted } from '../payment/booking-completed';
import { BOOKING_COMPLETED_SEGMENT } from 'shared/consts/paths';
import { Checkout } from '../payment/checkout';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'store/router/actions';
import { useSearchParams } from 'hooks/use-search-params.hook';
import qs from 'qs';
import { ACTION_STATUSES } from 'shared/consts';
import { selectPatchTransactionsStatus } from 'store/transactions/selectors';
import useKioskEnabled from 'store/kiosk/hooks/use-kiosk-enabled';

interface PaymentContentProps {
  bookingId?: number | string;
  reverseAnimation: boolean;
  closeQuickCheckoutHandler: VoidFunction;
  isCancelBookingStep: boolean;
  setIsCancelBookingStep: React.Dispatch<React.SetStateAction<boolean>>;
  onCloseReceipt: VoidFunction;
  onPressBack: VoidFunction;
  onCloseCheckout: VoidFunction;
  openCheckout: (expandAddCard?: boolean) => void;
  openReceipt: VoidFunction;
  overflow?: string;
  showNativeMobileHeader?: boolean;
  isFullscreen?: boolean;
  /*
   * RES-1367 — which surface the post-booking add-on step opens on.
   *
   * ⛔ DEFAULTS TO THE SHEET, and that default is the guard rather than a preference: this component
   * is mounted at nine sites and only ONE of them lays the booking out in columns. The sheet is the
   * right shape everywhere the tenant is mid-checkout and the step IS the page; it is the wrong one
   * on my-bookings, where it opened as a fixed-width modal across three panes it knew nothing about.
   *
   * ⚠️ The step itself does not change with this — see `BookingAddOnsDialog`, which drives the same
   * `useBookingAddOns` the sheet does. Only the chrome around it does.
   */
  addOnsSurface?: 'sheet' | 'dialog';
}

/*
 * RES-1407 — the add-on step's path from wherever its door is: under the 2-way chat redesign the
 * post-booking route (`…/booked`, its last segment swapped), otherwise the old receipt, rewritten
 * exactly as it always was.
 */
const BOOKED_TAIL = new RegExp(`/${BOOKING_COMPLETED_SEGMENT}/?$`);

export const addOnsPathFrom = (pathname: string): string =>
  BOOKED_TAIL.test(pathname)
    ? pathname.replace(BOOKED_TAIL, `/${BOOKING_ADD_ONS_SEGMENT}`)
    : pathname.replace('receipt', BOOKING_ADD_ONS_SEGMENT);

export const PaymentContent = ({
  bookingId,
  reverseAnimation,
  closeQuickCheckoutHandler,
  isCancelBookingStep,
  setIsCancelBookingStep,
  onPressBack,
  onCloseReceipt,
  openCheckout,
  onCloseCheckout,
  openReceipt,
  overflow,
  showNativeMobileHeader = true,
  isFullscreen = false,
  addOnsSurface = 'sheet',
}: PaymentContentProps): JSX.Element => {
  const isMobileDevice = useIsSmallViewportWidth();
  const isKioskMode = useKioskEnabled();
  const client = useMiniappSdk();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const queryParams = useSearchParams();
  const { enable2WayChatRedesignResourceBooking } = useFlags();
  const patchTransactionsStatus = useSelector(selectPatchTransactionsStatus);
  const hideHeader = useMemo<boolean>(
    /*
     * RES-1050: the add-on step is a sheet like the receipt is, so it needs the native header hidden for
     * the same reason. Without this the host header renders over the sheet on mobile.
     */
    () =>
      (pathname.includes('checkout') ||
        pathname.includes('receipt') ||
        /* RES-1407: `booked` stands where `receipt` did, and is the same sheet. */
        BOOKED_TAIL.test(pathname) ||
        /*
         * ⚠️ The DIALOG surface is excluded. Hiding the host header is what the sheet needs, because
         * the host would otherwise draw over it; the dialog is a Radix overlay with its own backdrop
         * and its own dismissals, and taking the host's header away there removes a way out of the
         * page UNDERNEATH without putting one back.
         */
        (pathname.includes(BOOKING_ADD_ONS_SEGMENT) && addOnsSurface === 'sheet')) &&
      isMobileDevice,
    [pathname, isMobileDevice, addOnsSurface],
  );

  useEffect(() => {
    if (hideHeader) {
      client?.header.hideHeader();
      client?.swipe.disableBackSwipe();
    }

    return () => {
      if (patchTransactionsStatus !== ACTION_STATUSES.PENDING && showNativeMobileHeader) {
        client?.header.showHeader();
        client?.swipe.enableBackSwipe();
      }
    };
  }, [client, isMobileDevice, hideHeader, patchTransactionsStatus, showNativeMobileHeader]);

  /*
   * RES-1326 — the receipt's door into the add-on step.
   *
   * ⭐ SUPPLIED ONLY TO THE `receipt` ROUTE BELOW, and that is the guard rather than tidiness: the same
   * `Receipt` is also rendered by `receiptPage.tsx` for the `receipt/:transactionUuid` deep link, which
   * sits OUTSIDE any `PaymentContent` and therefore has no `add-on-request` sibling to reach. The prompt
   * card renders nothing when this prop is absent, so it cannot offer a CTA with no destination.
   *
   * ⭐ `push`, not `replace`. The old detour replaced, correctly — you must not be able to go "back" into
   * a completed checkout. The receipt is now a legitimate back target, so pushing leaves it in history
   * and the browser back button returns to it.
   */
  const onBrowseAddOns = useCallback(
    (addOnBookingId: string) => {
      dispatch(
        push(
          `${addOnsPathFrom(pathname)}?${qs.stringify({
            ...queryParams,
            [BOOKING_ADD_ONS_PARAM]: addOnBookingId,
          })}`,
        ),
      );
    },
    [dispatch, pathname, queryParams],
  );

  const receipt = (
    <Receipt
      transactionId={bookingId}
      onCloseReceipt={onCloseReceipt}
      isCancelBookingStep={isCancelBookingStep}
      setIsCancelBookingStep={setIsCancelBookingStep}
      isFullscreen={isFullscreen}
      onBrowseAddOns={onBrowseAddOns}
    />
  );

  return (
    <Routes>
      <Route
        path="quick-checkout"
        element={
          <QuickCheckout
            closeQuickCheckoutHandler={closeQuickCheckoutHandler}
            reverseAnimation={reverseAnimation}
            openCheckout={openCheckout}
            overflow={overflow}
          />
        }
      />
      <Route
        path="checkout"
        element={
          <ReceiptBooking
            content={<Checkout openReceipt={openReceipt} onCloseCheckout={onCloseCheckout} />}
            onClose={onCloseCheckout}
            withLeftArrowButton={isCancelBookingStep}
            onPressBack={onPressBack}
          />
        }
      />
      {/*
        RES-1050 — the post-booking add-on step, and RES-1336 — it shares ONE sheet with the receipt.

        ⛔ A LAYOUT ROUTE, NOT TWO SIBLINGS, and that is the whole point. As siblings, stepping from the
        receipt into the add-ons unmounted one ReceiptBooking and mounted another — measured in the
        browser, the wrapper's DOM node does not survive the transition — so the tenant saw the sheet
        slide away and a second one slide back in, rather than the content changing. One element here
        keeps the sheet mounted and swaps only what is inside it.

        Mounted HERE rather than in `router.tsx` so it inherits every prefix PaymentContent is mounted
        under; the receipt is a nested segment, not a top-level route. (`router.tsx`'s
        `receipt/:transactionUuid` is the separate uuid deeplink.)

        The shell keeps the receipt's props. The add-on step overrides what differs — its title, where
        back goes, and that it wants a desktop modal — through the step header, because those differ
        per SCREEN and a prop on the shell can only say one thing for all of them. See
        `pages/booking-add-ons/step-header.context`.
      */}
      {/*
        RES-1367 — the dialog surface is its OWN route, outside the shell.

        ⛔ IT MUST NOT BE NESTED. The layout route below exists to keep ONE `ReceiptBooking` mounted
        across the receipt→add-ons step, so the sheet's content swaps instead of the sheet sliding
        away and a second one sliding back in. The dialog brings its own surface and its own header,
        so nesting it would wrap a Radix overlay in the very sheet it replaces.

        ⚠️ React Router skips a `false` child, so this and the nested route below are mutually
        exclusive by construction rather than by a path that happens not to match.
      */}
      {addOnsSurface === 'dialog' && <Route path={BOOKING_ADD_ONS_SEGMENT} element={<BookingAddOnsDialog />} />}
      <Route
        element={
          <StepHeaderProvider>
            <ReceiptBooking
              content={<Outlet />}
              onClose={onCloseReceipt}
              withLeftArrowButton={isCancelBookingStep}
              onPressBack={onPressBack}
              isFullscreen={isFullscreen}
              disableSwipeModal={enable2WayChatRedesignResourceBooking}
            />
          </StepHeaderProvider>
        }
      >
        {addOnsSurface === 'sheet' && <Route path={BOOKING_ADD_ONS_SEGMENT} element={<BookingAddOnsPage />} />}
        {/*
          RES-1407 — where every checkout lands under the 2-way chat redesign: the add-on offer
          (Booking confirmed) or straight to My bookings, never the old receipt. Inside the same shell as
          the receipt and the add-on step, so the step's back arrow returns here without a new sheet.
        */}
        <Route
          path={BOOKING_COMPLETED_SEGMENT}
          /* Kiosk never left the receipt behind: it keeps exactly what it had. */
          element={isKioskMode ? receipt : <BookingCompleted onBrowseAddOns={onBrowseAddOns} />}
        />
        <Route path="receipt" element={receipt} />
      </Route>
    </Routes>
  );
};
